import { useState, useEffect, useCallback } from 'react';
import { pushApi, portalPushApi, PushSubscriptionRequest } from '@/api/endpoints/push.api';

type PushPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

interface UsePushNotificationsOptions {
  /**
   * Se true, usa a API do portal (pacientes). Default: false (dashboard).
   */
  isPortal?: boolean;

  /**
   * Nome opcional do dispositivo para identificação.
   */
  deviceName?: string;

  /**
   * Se deve registrar automaticamente quando permissão for concedida.
   */
  autoSubscribe?: boolean;
}

interface UsePushNotificationsResult {
  /**
   * Se push notifications são suportadas pelo browser.
   */
  isSupported: boolean;

  /**
   * Estado atual da permissão.
   */
  permission: PushPermissionState;

  /**
   * Se o usuário está inscrito para receber notificações.
   */
  isSubscribed: boolean;

  /**
   * Se está carregando (verificando status ou inscrevendo).
   */
  isLoading: boolean;

  /**
   * Erro, se houver.
   */
  error: string | null;

  /**
   * Solicita permissão ao usuário.
   */
  requestPermission: () => Promise<boolean>;

  /**
   * Inscreve o usuário para receber notificações.
   */
  subscribe: () => Promise<boolean>;

  /**
   * Remove a inscrição do usuário.
   */
  unsubscribe: () => Promise<boolean>;

  /**
   * Atualiza o status da inscrição.
   */
  refresh: () => Promise<void>;
}

/**
 * Hook para gerenciar Web Push Notifications.
 *
 * @example
 * ```tsx
 * const { isSupported, permission, isSubscribed, subscribe, unsubscribe } = usePushNotifications();
 *
 * if (!isSupported) {
 *   return <p>Seu navegador não suporta notificações push.</p>;
 * }
 *
 * if (permission === 'denied') {
 *   return <p>Você bloqueou as notificações. Habilite nas configurações do navegador.</p>;
 * }
 *
 * return (
 *   <Button onClick={isSubscribed ? unsubscribe : subscribe}>
 *     {isSubscribed ? 'Desativar notificações' : 'Ativar notificações'}
 *   </Button>
 * );
 * ```
 */
export function usePushNotifications(options: UsePushNotificationsOptions = {}): UsePushNotificationsResult {
  const { isPortal = false, deviceName, autoSubscribe = false } = options;

  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<PushPermissionState>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);

  const api = isPortal ? portalPushApi : pushApi;

  // Verifica suporte do browser
  useEffect(() => {
    const checkSupport = () => {
      const supported =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;

      setIsSupported(supported);

      if (supported) {
        setPermission(Notification.permission as PushPermissionState);
      } else {
        setPermission('unsupported');
      }
    };

    checkSupport();
  }, []);

  // Carrega a chave VAPID e verifica status
  useEffect(() => {
    const initialize = async () => {
      if (!isSupported) {
        setIsLoading(false);
        return;
      }

      try {
        // Buscar chave VAPID
        const { publicKey } = await api.getVapidPublicKey();
        setVapidPublicKey(publicKey);

        // Verificar se já está inscrito
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();

        if (existingSubscription) {
          // Verificar no servidor se a subscription está ativa
          try {
            const status = await api.getStatus();
            setIsSubscribed(status.subscribed);
          } catch {
            // Se falhar, considera como não inscrito
            setIsSubscribed(false);
          }
        } else {
          setIsSubscribed(false);
        }
      } catch (err) {
        console.error('Erro ao inicializar push notifications:', err);
        setError('Erro ao carregar configurações de notificação');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [isSupported, api]);

  // Auto-subscribe quando permissão é concedida
  useEffect(() => {
    if (autoSubscribe && permission === 'granted' && !isSubscribed && !isLoading && vapidPublicKey) {
      subscribe();
    }
  }, [autoSubscribe, permission, isSubscribed, isLoading, vapidPublicKey]);

  /**
   * Solicita permissão ao usuário.
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setError('Notificações não são suportadas neste navegador');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result as PushPermissionState);
      return result === 'granted';
    } catch (err) {
      console.error('Erro ao solicitar permissão:', err);
      setError('Erro ao solicitar permissão para notificações');
      return false;
    }
  }, [isSupported]);

  /**
   * Converte a chave VAPID de Base64 URL-safe para Uint8Array.
   */
  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  };

  /**
   * Inscreve o usuário para receber notificações.
   */
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported || !vapidPublicKey) {
      setError('Push notifications não disponíveis');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Verificar/solicitar permissão
      if (Notification.permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) {
          setError('Permissão negada para notificações');
          setIsLoading(false);
          return false;
        }
      }

      // Obter service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Criar subscription
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey.buffer as ArrayBuffer,
      });

      // Extrair dados da subscription
      const subscriptionJson = subscription.toJSON();

      const subscriptionRequest: PushSubscriptionRequest = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: subscriptionJson.keys?.p256dh || '',
          auth: subscriptionJson.keys?.auth || '',
        },
        userAgent: navigator.userAgent,
        deviceName: deviceName,
      };

      // Registrar no servidor
      await api.subscribe(subscriptionRequest);

      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error('Erro ao inscrever para push notifications:', err);
      setError('Erro ao ativar notificações');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, vapidPublicKey, deviceName, api, requestPermission]);

  /**
   * Remove a inscrição do usuário.
   */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Obter service worker registration
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remover do servidor primeiro
        await api.unsubscribe(subscription.endpoint);

        // Depois cancelar localmente
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.error('Erro ao cancelar inscrição:', err);
      setError('Erro ao desativar notificações');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, api]);

  /**
   * Atualiza o status da inscrição.
   */
  const refresh = useCallback(async (): Promise<void> => {
    if (!isSupported) return;

    setIsLoading(true);
    try {
      const status = await api.getStatus();
      setIsSubscribed(status.subscribed);
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, api]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    refresh,
  };
}
